<template>
  <Loader @loadend="initConfigurator" />
  <div v-if="assetsLoaded">
    <HeaderBar ref="header" />
    <div id="configurator" class="flex gap-4 mx-4 mb-4">
      <div id="size-wrapper">
        <div id="config-navigation" class="grid grid-cols-3 items-center gap-1 mb-2">
          <ToggleButton :text="'Construction'" :class="this.building ? 'filled' : ''" @click="this.building = true" />
          <ArrowsRightLeftIcon class="h-6 w-6" />
          <ToggleButton :text="'Textures'" :class="!this.building ? 'filled' : ''" @click="this.building = false" />
        </div>
        <div v-if="this.building" id="config-content" class="column rounded-2xl p-4 bg-gray-100">
          <List :project="this.$store.state.currentConfiguration" :activeBox="this.activeBox" @newBox="newBox" @selectedBox="selectedBox" />
          <BoxConfig
            :project="this.$store.state.currentConfiguration"
            :activeBox="this.activeBox"
            @updateBox="updateBox"
            @newContent="newContent"
            @changeType="changeType"
            @setContentHeight="setContentHeight"
            @removeActiveBox="removeActiveBox"
            @removeAccessory="removeAccessory"
          />
        </div>
        <TextureConfig v-else class="column rounded-2xl bg-gray-200" @newGlobalColor="newGlobalColor" />
      </div>
      <p id="price" class="text-xl lg:text-2xl xl:text-4xl pointer-events-none">Montant actuel : {{ projectPrice }}€</p>
      <Webgl ref="webGl" />
      <GroundConfig @newGround="newGround" />
    </div>
  </div>
</template>

<script>
// UI components
import HeaderBar from '@/components/HeaderBar.vue';
import ToggleButton from '@/components/ui/ToggleButton.vue';
import List from '../components/configurator/List.vue';
import BoxConfig from '../components/configurator/BoxConfig.vue';
import Webgl from '../components/Webgl.vue';
import TextureConfig from '../components/configurator/TextureConfig.vue';
import Loader from '../components/Loader.vue';
import GroundConfig from '../components/configurator/GroundConfig.vue';

// Vanilla JavaScript files related to the 3D scene
import Dressing from '../utils/webgl/Dressing.js';
import Raycaster from '../utils/webgl/Raycaster.js';

// Icons
import { ArrowsRightLeftIcon } from '@heroicons/vue/24/outline';

// Vanilla JavaScript files
import { calculateConfigurationPrice } from '@/utils/utils.js';
import { decodeURL } from '@/utils/utils.js';

export default {
  name: 'Configurator',
  components: {
    HeaderBar,
    List,
    BoxConfig,
    Webgl,
    TextureConfig,
    ToggleButton,
    ArrowsRightLeftIcon,
    Loader,
    GroundConfig,
  },
  computed: {
    projectPrice() {
      return calculateConfigurationPrice(this.$store.state.currentConfiguration);
    },
  },
  data() {
    return {
      activeBox: 0,
      dressing: new Dressing(),
      raycaster: new Raycaster(),
      building: true,
      colorList: [
        '#F48FB133',
        '#CE93D833',
        '#B39DDB33',
        '#9FA8DA33',
        '#90CAF933',
        '#81D4FA33',
        '#80DEEA33',
        '#80CBC433',
        '#A5D6A733',
        '#C5E1A533',
        '#E6EE9C33',
        '#FFF59D33',
        '#FFCC8033',
        '#FFAB9133',
        '#BCAAA433',
        '#E5737333',
      ],
      colorID: 0,
      assetsLoaded: false,
    };
  },
  methods: {
    newBox(width = 64, height = 194) {
      this.$store.commit('addBox', { width: width, height: height, color: this.getColor() });

      this.activeBox = this.$store.state.currentConfiguration.length - 1;

      this.dressing.newBox(width, height);
      this.raycaster.refreshLists();
      this.$refs.header.currentConfigurationChanged();
    },
    selectedBox(position) {
      this.activeBox = position;
    },
    getColor() {
      const hex = this.colorList[this.colorID];

      this.colorID += 1;
      if (this.colorID > this.colorList.length - 1) {
        this.colorID = 0;
      }

      return hex;
    },
    updateBox(parameter, value) {
      this.$store.commit('modifyBox', { activeBox: this.activeBox, parameter: parameter, value: value });

      this.dressing.scaleBox(this.activeBox, parameter, value);
      this.raycaster.refreshLists();
      this.$refs.header.currentConfigurationChanged();
    },
    newContent(key) {
      this.$store.commit('newContent', { activeBox: this.activeBox, key: key });

      this.dressing.newContent(this.activeBox, key);
      this.$refs.header.currentConfigurationChanged();
    },
    changeType(isBox) {
      this.$store.commit('changeType', { activeBox: this.activeBox, isBox: isBox });
    },
    setContentHeight(id, value) {
      this.$store.commit('setContentHeight', { activeBox: this.activeBox, id: id, value: value });

      this.dressing.updateContentHeight(this.activeBox, id, value);
      this.$refs.header.currentConfigurationChanged();
    },
    removeActiveBox() {
      this.$store.commit('removeActiveBox', { activeBox: this.activeBox });

      this.dressing.removeBox(this.activeBox);
      this.raycaster.refreshLists();

      if (this.activeBox > 0) {
        this.activeBox -= 1;
      }

      this.$refs.header.currentConfigurationChanged();
    },
    removeAccessory(accessoryID) {
      this.$store.commit('removeAccessory', { activeBox: this.activeBox, accessoryID: accessoryID });

      this.dressing.removeAccessory(this.activeBox, accessoryID);
      this.$refs.header.currentConfigurationChanged();
    },
    newGlobalColor(colorID) {
      this.$store.commit('updateGlobalColor', { colorID: colorID });
      this.$refs.header.currentConfigurationChanged();
    },
    initConfigurator() {
      this.assetsLoaded = true;

      this.$nextTick(() => {
        this.dressing.resetInstance();

        // Try to load the configuration from the URL
        const parsedConfiguration = decodeURL(window.location.search);
        if (parsedConfiguration.length > 0) {
          // Since no list color is restored, give new colors to each box
          for (let box of parsedConfiguration) {
            box.color = this.getColor();
          }

          // If a configuration has been found in the URL, load it in the currentConfiguration of the store
          this.$store.commit('updateCurrentConfiguration', { currentConfiguration: parsedConfiguration });
          this.loadPreviousConfiguration();

          this.$router.push(this.$route.path); // Clear the query parameters string
        } else if (this.$store.state.lastChosenPreset != null) {
          // Load the selected configuration if the user defined one as such in the previous page
          this.loadPresetConfiguration();
        } else if (this.$store.state.currentConfiguration.length > 0) {
          // Load the last configuration the user was editing
          this.loadPreviousConfiguration();
        } else {
          // The user wanted to make a project from scratch, create only one default box
          this.newBox();
        }

        const local = this;

        function refreshUICurrentBox(newID) {
          local.activeBox = newID;
        }

        function getUIid() {
          return local.activeBox;
        }

        this.dressing.refreshUICurrentBox = refreshUICurrentBox;
        this.dressing.getUIid = getUIid;
      });
    },
    loadPresetConfiguration() {
      // Erase the previous saved configuration
      this.$store.commit('clearCurrentConfiguration');

      // Recreate the configuration structure
      for (const box of this.$store.state.lastChosenPreset['currentConfiguration']) {
        this.newBox(box.width);
      }

      // Erase the selected preset configuration from the store
      this.$store.commit('clearChosenPreset');

      // Focus the first box
      this.activeBox = 0;

      this.$refs.header.currentConfigurationChanged();
    },
    loadPreviousConfiguration() {
      this.dressing.restoreInstance(this.$store.state.currentConfiguration);
      this.raycaster.refreshLists();

      // Focus the first box
      this.activeBox = 0;

      this.$refs.header.currentConfigurationChanged();
    },
    newGround(ground) {
      this.$refs.webGl.newGround(ground);
    },
  },
};
</script>

<style scoped>
.selected {
  background-color: #993973 !important;
}

#config-navigation {
  grid-template-columns: 1fr auto 1fr;
}

#config-content {
  height: calc(100% - 3.25rem);
  overflow-y: auto;
}

#price {
  position: absolute;

  bottom: 0;
  right: 0;
  margin: 2rem;
}

@media screen and (orientation: landscape) {
  #configurator {
    flex-direction: row;

    /* The website header is 75px tall + 1rem/16px bottom margin */
    height: calc(100vh - 91px);
  }

  #size-wrapper {
    width: calc((100vw - 3rem) / 2);
  }
}

@media screen and (orientation: portrait) {
  #configurator {
    flex-direction: column;
  }

  #size-wrapper {
    /* The website header is 75px tall + 1rem/16px bottom margin */
    height: calc((100vh - 91px) / 2);
  }
}
</style>
